// Inline export of the property list (Search Properties screen).
//
// Fetches property metadata directly from Supabase, outputs XLSX/CSV/JSON.

import { supabase } from "@/shared/integrations/supabase/client";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import { getAppOriginUrl } from "@/shared/lib/appBasePath";
import * as XLSX from "xlsx-js-style";
import { chunk, csvCell, triggerDownload } from "./_helpers";
import { downloadWorkbook } from "./_xlsx";
import { getStatusLabel } from "@/features/properties/services/deliveryStatusConfig";

export type PropertyExportFormat = "xlsx" | "csv" | "json";

// Bound the survey-date `.in(...)` lookup so a whole-portfolio export URL stays
// under limits. Lightweight rows (2 columns), so a larger batch is fine.
const SURVEY_DATE_PROPERTY_BATCH = 200;

export interface PropertyExportFilters {
  projectId: string;
  search?: string;
  state?: string;
}

export interface PropertyExportOptions {
  filters: PropertyExportFilters;
  label: string;
  format: PropertyExportFormat;
}

interface PropertyRow {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  store_number: string | null;
  square_footage: string | null;
  delivery_status: string | null;
}

export async function exportPropertyList(opts: PropertyExportOptions): Promise<void> {
  const { filters, label, format } = opts;

  const properties = (
    await fetchClientProperties<PropertyRow>(
      filters.projectId,
      "id, name, address, city, state, zip_code, store_number, square_footage, delivery_status",
      (q) => {
        if (filters.search)
          q = q.or(`name.ilike.%${filters.search}%,address.ilike.%${filters.search}%`);
        if (filters.state) q = q.eq("state", filters.state);
        return q;
      },
    )
  ).sort((a, b) => {
    // store_number ascending, nulls last (matches the prior DB ordering)
    if (a.store_number == null && b.store_number == null) return 0;
    if (a.store_number == null) return 1;
    if (b.store_number == null) return -1;
    return String(a.store_number).localeCompare(String(b.store_number), undefined, {
      numeric: true,
    });
  });

  // Fetch latest survey date per property from property_attributes (same source as properties page)
  const propertyIds = properties.map((p) => p.id);
  const surveyDateMap: Record<string, string> = {};
  // Chunk the `.in(...)` so a whole-portfolio export doesn't build an oversized
  // request URL. Each property stays wholly within one chunk (latest-per-property).
  for (const idChunk of chunk(propertyIds, SURVEY_DATE_PROPERTY_BATCH)) {
    const { data: surveys } = await (supabase as unknown as any)
      .from("property_attributes")
      .select("property_id, captured_at")
      .in("property_id", idChunk)
      .order("captured_at", { ascending: false });

    if (surveys) {
      for (const s of surveys as { property_id: string; captured_at: string }[]) {
        if (!surveyDateMap[s.property_id]) {
          surveyDateMap[s.property_id] = s.captured_at;
        }
      }
    }
  }

  // Format ISO timestamp to YYYY-MM-DD for Excel compatibility
  const formatDate = (iso: string | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  // Use shared status labels
  const formatStatus = (s: string | null): string =>
    s ? getStatusLabel(s) : "";

  const safeLabel = label.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  const baseUrl = typeof window !== "undefined" ? getAppOriginUrl() : "";
  const propertyUrl = (id: string) =>
    `${baseUrl}/property/${id}`;

  if (format === "json") {
    const withUrls = properties.map((p) => ({
      ...p,
      survey_date: formatDate(surveyDateMap[p.id]) || null,
      url: propertyUrl(p.id),
    }));
    triggerDownload(JSON.stringify(withUrls, null, 2), `${safeLabel}_properties.json`, "application/json");
    return;
  }

  const header: Array<string | number | null> = [
    "Store #", "Name", "Address", "City", "State", "ZIP",
    "Square Footage",
    "Survey Date", "Status", "Property URL",
  ];
  const rows: Array<Array<string | number | null>> = [header];
  for (const p of properties) {
    rows.push([
      p.store_number ?? "",
      p.name ?? "",
      p.address ?? "",
      p.city ?? "",
      p.state ?? "",
      p.zip_code ?? "",
      p.square_footage ?? "",
      formatDate(surveyDateMap[p.id]),
      formatStatus(p.delivery_status),
      propertyUrl(p.id),
    ]);
  }

  if (format === "csv") {
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
    triggerDownload(csv, `${safeLabel}_properties.csv`, "text/csv;charset=utf-8");
    return;
  }

  // XLSX
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1");
  for (let c = range.s.c; c <= range.e.c; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[ref]) continue;
    ws[ref].s = {
      font: { color: { rgb: "FFFFFF" }, bold: true },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { wrapText: true, vertical: "top" },
    };
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Properties");
  downloadWorkbook(wb, `${safeLabel}_properties.xlsx`);
}

